import { CheckCircle, CircleDashed, CircleNotch } from "@phosphor-icons/react";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, ProgressBar, formatAuditTime } from "@/components/common";
import { milestoneContent } from "@/lib/milestones";

const STATUS_META = {
  complete: { icon: CheckCircle, label: "Complete", className: "text-accent" },
  in_progress: { icon: CircleNotch, label: "In progress", className: "text-secondary" },
  upcoming: { icon: CircleDashed, label: "Upcoming", className: "text-muted-foreground/50" },
};

export default function Plan() {
  const { data: event, loading } = useGet("/client/event");
  if (loading) {
    return <Loading label="Loading your plan" />;
  }
  const milestones = event?.milestones || [];
  const complete = milestones.filter((milestone) => milestone.status === "complete").length;

  return (
    <div>
      <PageHeader
        eyebrow="Your planning journey"
        title="Plan"
        description="Your Gledswood team manages each milestone status as your event comes together."
      />
      <section className="border-y border-border py-5" data-testid="plan-progress-summary">
        <p className="eyebrow">Journey progress</p>
        <p className="font-heading text-3xl text-primary mt-1">
          {complete} of {milestones.length || 13}
        </p>
        <div className="mt-4 max-w-md">
          <ProgressBar
            value={complete}
            max={milestones.length || 13}
            label="Milestones completed"
            testid="plan-progress-bar"
          />
        </div>
      </section>
      <ol className="mt-6 divide-y divide-border border-y border-border" data-testid="plan-milestone-list">
        {milestones.map((milestone, index) => {
          const meta = STATUS_META[milestone.status] || STATUS_META.upcoming;
          const Icon = meta.icon;
          const content = milestoneContent(milestone);
          return (
            <li
              key={milestone.key}
              className="grid grid-cols-[2.5rem_1fr_auto] gap-3 py-5 sm:grid-cols-[3rem_1fr_auto]"
              data-testid={`plan-milestone-${milestone.key}`}
            >
              <span className="font-heading text-xl text-muted-foreground/70 pt-0.5">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h2 className="font-heading text-2xl text-primary leading-tight">{content.label}</h2>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{content.description}</p>
                {milestone.status === "complete" && milestone.completed_at && (
                  <p
                    className="mt-2 text-xs text-accent"
                    data-testid={`plan-milestone-completed-${milestone.key}`}
                  >
                    Completed {formatAuditTime(milestone.completed_at)}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 text-right">
                <Icon size={22} weight={milestone.status === "complete" ? "fill" : "regular"} className={meta.className} />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{meta.label}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}